import {
    formatIncomeSplitPercentagesNew,
    type IncomeSplitConfig,
    IncomeSplitDetails,
    PaycheckRoutingStatus,
} from '@fruitful/income-split';
import {
    AnalyticsUserEvent,
    type DepositAccount,
    type ImAccount,
} from '@fruitful/types';
import type { EventArg, NavigationAction } from '@react-navigation/native';
import { Redirect, router, useNavigation } from 'expo-router';
import { useAtom, useSetAtom } from 'jotai';
import { cloneDeep, sortBy } from 'lodash';
import React, { useEffect, useRef, useState } from 'react';
import { NestableScrollContainer } from 'react-native-draggable-flatlist';
import {
    ArrowSplit,
    BodyMediumRegular,
    Button,
    ButtonSize,
    ButtonVariant,
    CirclePlusIcon,
    EditIcon,
    isNonMobileWeb,
    isWeb,
    ScreenContainer,
    ScrollView,
    Spinner,
    styled,
    Switch,
    View,
    YStack,
} from 'smoothie';

import { useAuthedUser } from '../../../common/providers/AuthedUserProvider';
import { useViewEvents } from '../../../events/hooks/useViewEvents';
import useGetIncomeSplitFromGlobalState from '../../hooks/income-split/useGetIncomeSplitFromGlobalStateNew';
import { useInternalInvestmentAccountsForIncomeSplit } from '../../hooks/income-split/useInternalInvestmentAccountsForIncomeSplit';
import useShouldSeeIncomeSplit from '../../hooks/income-split/useShouldSeeIncomeSplit';
import useUpdateIncomeSplitStatus from '../../hooks/income-split/useUpdateIncomeSplitStatusNew';
import useGetAllBankAccountDetailsFromGlobalState from '../../hooks/useGetAllBankAccountDetailsFromGlobalState';
import useGetInternalAndExternalDepositAccounts from '../../hooks/useGetInternalAndExternalDepositAccounts';
import {
    incomeSplitAtom,
    incomeSplitRuleSetEditorModeAtom,
} from '../../state/PaycheckRoutingAtoms';
import { isAccountValidAsIncomeSplitSource } from '../../utils/IncomeSplitUtils';
import IncomeSplitEditRuleSetSheet from './IncomeSplitEditRuleSetSheet';
import IncomeSplitRules from './IncomeSplitRulesNew';
import IncomeSplitToggleSheet from './IncomeSplitToggleSheetNew';
import IncomeSplitWelcomeSheet from './info/IncomeSplitWelcome';

const IncomeSplitBox = styled(View, {
    paddingVertical: 16,
    paddingHorizontal: 16,
    borderRadius: 16,
    backgroundColor: '$gray200',
});

const Header = () => {
    return (
        <BodyMediumRegular>
            Rules will execute in order from top to bottom subject to available
            funds.
        </BodyMediumRegular>
    );
};

const Toggle = ({ openToggleSheet }: { openToggleSheet: () => void }) => {
    const [incomeSplit, setIncomeSplit] = useAtom(incomeSplitAtom);

    return (
        <IncomeSplitBox
            flexDirection="row"
            alignItems="center"
            justifyContent="space-between"
        >
            <BodyMediumRegular>
                Income Split is{' '}
                {incomeSplit?.status === PaycheckRoutingStatus.ACTIVE
                    ? 'on'
                    : 'off'}
            </BodyMediumRegular>
            <Switch
                isChecked={incomeSplit?.status === PaycheckRoutingStatus.ACTIVE}
                onCheckedChange={(checked) => {
                    if (!incomeSplit) return;
                    setIncomeSplit({
                        ...incomeSplit,
                        status: checked
                            ? PaycheckRoutingStatus.ACTIVE
                            : PaycheckRoutingStatus.INACTIVE,
                    });
                    openToggleSheet();
                }}
                style={{
                    transformOrigin: 'right',
                }}
            />
        </IncomeSplitBox>
    );
};

const Rules = ({
    incomeSplit,
    externalAccounts,
    internalInvestmentAccounts,
}: {
    incomeSplit: IncomeSplitConfig;
    externalAccounts: DepositAccount[];
    internalInvestmentAccounts?: ImAccount[];
}) => {
    const [isSorting, setIsSorting] = useState(false);

    const [isAddRuleSetSheetOpen, setIsAddRuleSetSheetOpen] = useState(false);
    const setIncomeSplitRuleSetEditorMode = useSetAtom(
        incomeSplitRuleSetEditorModeAtom,
    );

    return (
        <YStack flex={1} gap={16}>
            <YStack alignItems="center">
                <ArrowSplit />
            </YStack>
            <YStack flexShrink={1} flexGrow={1} paddingBottom={19}>
                <IncomeSplitRules
                    incomeSplit={incomeSplit}
                    isSorting={isSorting}
                    setIsSorting={setIsSorting}
                    externalAccounts={externalAccounts}
                    internalInvestmentAccounts={internalInvestmentAccounts}
                    setEditRuleSheetOpen={setIsAddRuleSetSheetOpen}
                />
                <View cursor="pointer">
                    <Button
                        variant={ButtonVariant.SECONDARY}
                        size={ButtonSize.LARGE}
                        text="Add rule set"
                        icon={CirclePlusIcon}
                        onPress={() => {
                            setIncomeSplitRuleSetEditorMode('Add');
                            setIsAddRuleSetSheetOpen(true);
                        }}
                    />
                </View>
            </YStack>
            <IncomeSplitEditRuleSetSheet
                isOpen={isAddRuleSetSheetOpen}
                onClose={() => {
                    setIsAddRuleSetSheetOpen(false);
                }}
            />
        </YStack>
    );
};

const IncomeSplitNew = ({ welcome }: { welcome?: boolean }) => {
    const user = useAuthedUser();
    const { trackViewOnce } = useViewEvents();
    const navigation = useNavigation();
    const { incomeSplit, isLoading: isLoadingIncomeSplit } =
        useGetIncomeSplitFromGlobalState();
    const { bankAccountDetails, isLoading: isLoadingBankAccountDetails } =
        useGetAllBankAccountDetailsFromGlobalState();
    const { mutateAsync: updateStatus } = useUpdateIncomeSplitStatus();

    const { data: depositAccounts, isLoading: isLoadingDepositAccounts } =
        useGetInternalAndExternalDepositAccounts({});

    const externalAccounts = (depositAccounts ?? []).filter(
        (d) => d.isExternal,
    );

    const {
        internalInvestmentAccounts,
        isLoading: isLoadingInternalInvestmentAccounts,
    } = useInternalInvestmentAccountsForIncomeSplit();

    const setIncomeSplit = useSetAtom(incomeSplitAtom);
    const [isWelcomeSheetOpen, setIsWelcomeSheetOpen] = useState(
        welcome ?? false,
    );
    const [isToggleSheetOpen, setIsToggleSheetOpen] = useState(false);

    // store a copy of the original income split configuration, so we can reset the income split atom
    // when going back without saving (and use both values to detect a diff and prompt members to take action)
    const originalIncomeSplit = useRef<IncomeSplitConfig | null>(null);
    useEffect(() => {
        if (incomeSplit && !originalIncomeSplit.current) {
            originalIncomeSplit.current = Object.freeze(cloneDeep(incomeSplit));
        }
    }, [incomeSplit]);

    // reset unsaved changes on going back via swiping or back button in header
    useEffect(() => {
        const handleBeforeRemove = (
            e: EventArg<'beforeRemove', true, { action: NavigationAction }>,
        ) => {
            if (
                e.data.action.type === 'GO_BACK' ||
                e.data.action.type === 'REPLACE'
            ) {
                setIncomeSplit(originalIncomeSplit.current);
            }
        };

        navigation.addListener('beforeRemove', handleBeforeRemove);

        return () => {
            navigation.removeListener('beforeRemove', handleBeforeRemove);
        };
    }, []);

    const shouldSeeIncomeSplit = useShouldSeeIncomeSplit();

    if (!shouldSeeIncomeSplit) return <Redirect href="/" />;

    const hasChanges = (): boolean => {
        const stripIdsAndSort = (incomeSplit: IncomeSplitConfig) => {
            return {
                accounts: sortBy(
                    incomeSplit.accounts?.map((account) => {
                        const { id, ...rest } = account;
                        return rest;
                    }),
                    'accountId',
                ),
                ruleSets: sortBy(
                    incomeSplit.ruleSets.map((set) => {
                        const { id, ...rest } = set;
                        return rest;
                    }),
                    'priority',
                ),
            };
        };

        if (!incomeSplit || !originalIncomeSplit.current) return false;

        const rulesHaveChanged =
            JSON.stringify(stripIdsAndSort(incomeSplit)) !==
            JSON.stringify(stripIdsAndSort(originalIncomeSplit.current));
        const incomeTypeHasChanged =
            incomeSplit.onlyExecuteOnPaychecks !==
            originalIncomeSplit.current.onlyExecuteOnPaychecks;

        return rulesHaveChanged || incomeTypeHasChanged;
    };

    const updateIncomeSplitStatus = async (status: PaycheckRoutingStatus) => {
        await updateStatus(
            {
                body: {
                    status,
                },
                urlParams: {
                    configId: incomeSplit?.id as string,
                },
            },
            {
                onSuccess: (result) => {
                    const formattedIncomeSplit =
                        formatIncomeSplitPercentagesNew(result);
                    originalIncomeSplit.current = formattedIncomeSplit;
                    setIncomeSplit(formattedIncomeSplit);
                    setIsToggleSheetOpen(false);
                },
            },
        );
    };

    const CustomScrollComponent = isWeb ? ScrollView : NestableScrollContainer;

    if (
        isLoadingIncomeSplit ||
        isLoadingBankAccountDetails ||
        !incomeSplit ||
        isLoadingDepositAccounts ||
        isLoadingInternalInvestmentAccounts
    ) {
        return <Spinner />;
    }

    trackViewOnce(AnalyticsUserEvent.INCOME_SPLIT_RULES_VIEW);

    return (
        <>
            <ScreenContainer
                title="Income Split"
                hasBackButton={true}
                onBack={() => {
                    router.back();
                }}
                maxWidthOverride={isNonMobileWeb ? 700 : undefined}
                scrollContainerOverride={(children) => (
                    <CustomScrollComponent
                        showsVerticalScrollIndicator={isWeb}
                        contentContainerStyle={{
                            flexGrow: 1,
                            paddingHorizontal: isNonMobileWeb ? 0 : 24,
                            width: '100%',
                            maxWidth: isNonMobileWeb ? 700 : undefined,
                            alignSelf: 'center',
                            paddingBottom: 24,
                        }}
                    >
                        {children}
                    </CustomScrollComponent>
                )}
            >
                <YStack flex={1} gap={24} justifyContent="space-between">
                    <YStack gap={16} flex={1} paddingTop={8}>
                        <Header />
                        {!incomeSplit.isNew && !hasChanges() && (
                            <Toggle
                                openToggleSheet={() => {
                                    setIsToggleSheetOpen(true);
                                }}
                            />
                        )}
                        <IncomeSplitDetails
                            incomeSplit={incomeSplit}
                            totalEligibleAccounts={bankAccountDetails.filter(
                                (account) =>
                                    isAccountValidAsIncomeSplitSource(
                                        user,
                                        account,
                                    ),
                            )}
                            icon={EditIcon}
                            onIconPress={() => {
                                router.navigate(
                                    '/income-split/settings?tiedRules=true',
                                );
                            }}
                        />
                        <Rules
                            incomeSplit={incomeSplit}
                            externalAccounts={externalAccounts}
                            internalInvestmentAccounts={
                                internalInvestmentAccounts ?? []
                            }
                        />
                    </YStack>

                    <Button
                        text="Continue"
                        variant={ButtonVariant.PRIMARY}
                        disabled={
                            !originalIncomeSplit.current ||
                            !hasChanges() ||
                            incomeSplit.ruleSets.length === 0 ||
                            incomeSplit.ruleSets.some(
                                (group) => group.rules.length === 0,
                            ) ||
                            !incomeSplit.ruleSets.every(
                                (set) =>
                                    set.rules.reduce(
                                        (sum, r) => sum + (r?.percentage ?? 0),
                                        0,
                                    ) === 100,
                            )
                        }
                        onPress={() => {
                            router.navigate(
                                '/income-split/review?tiedRules=true',
                            );
                        }}
                    />
                </YStack>
            </ScreenContainer>
            <IncomeSplitWelcomeSheet
                isOpen={isWelcomeSheetOpen}
                onClose={() => {
                    setIsWelcomeSheetOpen(false);
                }}
            />
            <IncomeSplitToggleSheet
                isOpen={isToggleSheetOpen}
                onSave={updateIncomeSplitStatus}
                onClose={() => {
                    setIsToggleSheetOpen(false);
                }}
                onCancel={() => {
                    setIsToggleSheetOpen(false);
                }}
            />
        </>
    );
};

export default IncomeSplitNew;
